/**
 * The API is allowed to sleep, so the app has to have something to say about it.
 *
 * `ca-jedi-<env>-api` runs at 0 to 2 replicas (runtime-topology's deployables
 * table), which is the cost shape dev and test are meant to have and is Erik's
 * standing call for both. The consequence is a user-visible one: the first read
 * after a quiet spell arrives at an ingress with nothing behind it, and the
 * browser waits while a replica starts, an image is pulled on a fresh build, the
 * host boots, and the first Postgres connection is opened.
 *
 * Nothing was wrong with that except what the person saw, which was NOTHING: the
 * shell gate rendered an empty `div.shell` while its first query was in flight,
 * and on the default dark theme an empty shell is a black screen. So a wait that
 * is ordinary and self-healing looked exactly like a broken app.
 *
 * This module is the small amount of policy that makes the wait legible: which
 * failures MEAN "asleep" rather than "broken", how long to keep trying, and
 * which environment to name while doing it. What it looks like is `WakingUp`.
 */

/**
 * The statuses a Container Apps ingress produces while there is no replica to
 * route to. 503 is the common one; 502 and 504 show up when a replica has
 * started but is not yet listening, or when activation outran the gateway.
 *
 * 429 is deliberately NOT here. Throttling means the API is awake and saying
 * no, and treating it as a wake would hide a real limit behind a nice screen.
 */
const WAKE_STATUSES = new Set([502, 503, 504])

/**
 * `fetch` rejects rather than resolving when the connection never completed,
 * which is what a cold ingress can also look like from the browser: a 503 with
 * no CORS headers on it is reported to script as a network failure, not as a
 * status. Matched on the message as well as the type because a `TypeError` from
 * our own code in a query function is not a wake, and retrying one a dozen times
 * would only make a real bug slower to surface.
 */
function isNetworkFailure(error: Error): boolean {
  return (
    error.name === 'TypeError' &&
    /failed to fetch|networkerror|load failed|network request failed/i.test(error.message)
  )
}

/**
 * Whether this failure is the environment being asleep.
 *
 * Reads the transport line `apiFetch` throws (`API 503 on /path: <body>`),
 * which is the only shape a non-ok response reaches the app as.
 */
export function isWaking(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  if (isNetworkFailure(error)) return true
  const status = Number(/^API (\d+) on \S+/.exec(error.message)?.[1])
  return WAKE_STATUSES.has(status)
}

/**
 * How long to keep trying before admitting it did not work.
 *
 * A steady retry rather than the library's exponential backoff, and the reason
 * is what is being waited for: a replica that will be ready at some unknown
 * moment in the next few seconds, not a service under load that needs backing
 * off. Exponential delay would routinely leave the app sitting idle for 30s
 * after the API had already come up.
 *
 * The count is a budget rather than a guess at the start-up time: most cold
 * starts never retry at all, because the ingress HOLDS the request while the
 * replica activates and answers it. Retries only happen when it fails fast, and
 * 12 of them at 2.5s covers the better part of a minute of that.
 */
const WAKE_RETRIES = 12
const WAKE_RETRY_DELAY_MS = 2_500

/**
 * The query client's retry policy. Anything that is not a wake keeps the one
 * retry the client has always had, so this widens nothing except the case it
 * was written for.
 */
export function retryWhileWaking(failureCount: number, error: unknown): boolean {
  return isWaking(error) ? failureCount < WAKE_RETRIES : failureCount < 1
}

export function retryDelayWhileWaking(failureCount: number, error: unknown): number {
  if (isWaking(error)) return WAKE_RETRY_DELAY_MS
  // The library's own default, restated because naming a `retryDelay` replaces it.
  return Math.min(1_000 * 2 ** failureCount, 30_000)
}

/*
  There WAS an `environmentName()` here, reading "dev" or "test" off the API host
  so the screen could open with "Waking the dev environment". It is gone rather
  than unused, and the reason is a rule and not a tidy-up: naming the deployment
  is right in exactly one environment and wrong in every other, and nobody who is
  waiting for the app needs to be told which copy of it they are waiting for.
  Anything that wants the environment should take it from configuration, not
  re-derive it from a hostname here.
*/
